/* eslint-disable max-statements */
import React from 'react';
import Button from 'src/Components/Button_V2';
import styles from './styles.module.scss';
import classNames from 'classnames';
import { CurrencyType, normalNumberToSystemCurrency, formatCurrency } from '@wolffun/system-currency';
import { useTypedDispatch, useTypedSelector } from 'src/redux/utils';
import { confirmStake, prepareConfirmStake } from 'src/redux/actions';
import { updateWalletBalance } from '@profile/src/redux/remoteActions';
import { StakingPlan } from 'src/models';
import { useStakeContext } from '../../../StakeProvider';
import icLoading from 'assets/svg/ic_loading_2.svg';
import icSuccess from 'assets/svg/ic_success_4.svg';
import icFailure from 'assets/svg/ic_failure_2.svg';
import icMaxPool from 'assets/svg/max_pool.svg';
import { getTotalPrices } from '../utils';
import HigherRewardContent from './HigherRewardContent';
import FlexibleStakeContent from './FlexibleStakeContent';
import ConfirmStake from './ConfirmStake';
import OptionTab from './OptionTab';
import { useUserWalletContext } from '@profile/src/Profile_V2/UserWalletProvider';
import { formatErrorMessage } from 'src/Pages/Staking/utils';
import { AppError } from 'src/redux/types';
import { usePopup } from 'src/Pages/Staking/PopupProvider';
import { useConfirmOpenWalletConnect } from 'src/Components/WalletConnectConfirmProvider';

const StakeForm = ({
  className
}) => {
  const dispatch = useTypedDispatch();
  const {
    confirmOpenWalletConnect
  } = useConfirmOpenWalletConnect();
  const {
    openMaintenance
  } = usePopup();
  const {
    chooseHero,
    tokenIds,
    resetState,
    selectAllOption,
    quickSelectedNumber
  } = useStakeContext();
  const {
    checkWalletError
  } = useUserWalletContext();
  const {
    endDate
  } = useTypedSelector(state => state.stakingConfig.data) || {};
  const [amountInput, setAmountInput] = React.useState(0);
  const error = useTypedSelector(state => state.confirmStake.error);
  const [activeTab, setActiveTab] = React.useState(0);
  const [planStake, setPlanStake] = React.useState(StakingPlan.ONE_YEAR);
  const numberSelected = (selectAllOption ? quickSelectedNumber : tokenIds === null || tokenIds === void 0 ? void 0 : tokenIds.length) || 0;
  const est = useTypedSelector(state => state.estimateApr.data);
  const thgPerHero = useTypedSelector(state => {
    var _state$stakingConfig$;

    return (_state$stakingConfig$ = state.stakingConfig.data) === null || _state$stakingConfig$ === void 0 ? void 0 : _state$stakingConfig$.thgPerHero;
  }) || {
    token: CurrencyType.THG,
    value: '0'
  };
  const thgBalance = useTypedSelector(state => state.connectWallet.thgBalance) || {
    token: CurrencyType.THG,
    value: '0'
  };
  const totalPrice = activeTab == 0 ? getTotalPrices(numberSelected, thgPerHero) : normalNumberToSystemCurrency(amountInput, CurrencyType.THG);
  let ActiveContent;

  (function (ActiveContent) {
    ActiveContent[ActiveContent["STAKE_FORM"] = 1] = "STAKE_FORM";
    ActiveContent[ActiveContent["CONFIRM_FORM"] = 2] = "CONFIRM_FORM";
    ActiveContent[ActiveContent["PROCESSING"] = 3] = "PROCESSING";
    ActiveContent[ActiveContent["SUCCESS"] = 4] = "SUCCESS";
    ActiveContent[ActiveContent["FAIL"] = 5] = "FAIL";
  })(ActiveContent || (ActiveContent = {}));

  const [showActiveContent, setShowActiveContent] = React.useState(ActiveContent.STAKE_FORM);

  const resetToInitState = () => {
    resetState();
    setPlanStake(StakingPlan.ONE_YEAR);
    setActiveTab(0);
  };

  const showStakeForm = () => {
    resetToInitState();
    setShowActiveContent(ActiveContent.STAKE_FORM);
  };

  const showConfirmForm = () => {
    setShowActiveContent(ActiveContent.CONFIRM_FORM);
  };

  const showProcessing = () => {
    setShowActiveContent(ActiveContent.PROCESSING);
  };

  const showSuccess = () => {
    setShowActiveContent(ActiveContent.SUCCESS);
  };

  const showFail = () => {
    setShowActiveContent(ActiveContent.FAIL);
  };

  const openChooseHero = () => {
    if (checkWalletError()) {
      return;
    } else chooseHero();
  };

  const doHigherRewardStake = async () => {
    showProcessing();
    selectAllOption === null || selectAllOption === void 0 ? void 0 : selectAllOption.desc;
    const preparedData = await dispatch(prepareConfirmStake({
      plan: planStake,
      systemPrice: totalPrice,
      tokenIds: tokenIds || [],
      desc: selectAllOption === null || selectAllOption === void 0 ? void 0 : selectAllOption.desc,
      isSelectAllTokens: selectAllOption === null || selectAllOption === void 0 ? void 0 : selectAllOption.isSelectAllTokens,
      sort: selectAllOption === null || selectAllOption === void 0 ? void 0 : selectAllOption.sort
    }));
    confirmOpenWalletConnect(async () => {
      const {
        error: confirmError
      } = await dispatch(confirmStake(preparedData));

      if (confirmError === AppError.MAINTENANCE_STAKING) {
        openMaintenance();
        return;
      }

      if (!confirmError) showSuccess();else {
        showFail();
      }
      dispatch(updateWalletBalance());
    });
  };

  const doFlexibleStake = async () => {
    showProcessing();
    const preparedData = await dispatch(prepareConfirmStake({
      plan: StakingPlan.FLEXIBLE,
      systemPrice: totalPrice,
      tokenIds: []
    }));
    confirmOpenWalletConnect(async () => {
      const {
        error: confirmError
      } = await dispatch(confirmStake(preparedData));

      if (confirmError === AppError.MAINTENANCE_STAKING) {
        openMaintenance();
        return;
      }

      if (!confirmError) showSuccess();else {
        showFail();
      }
      dispatch(updateWalletBalance());
    });
  };

  const renderProcessingContent = () => {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.processing
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.iconContainer
    }, /*#__PURE__*/React.createElement("img", {
      className: styles.rotate,
      src: icLoading
    }), /*#__PURE__*/React.createElement("span", null, "Processing...")), /*#__PURE__*/React.createElement("span", {
      className: styles.title
    }, "Transaction on chain"), /*#__PURE__*/React.createElement("span", {
      className: styles.description
    }, /*#__PURE__*/React.createElement("span", null, "Staking\xA0", formatCurrency(totalPrice, {
      withCurrency: true,
      trimWholeFraction: true
    }))));
  };

  const renderResultSuccessContent = () => {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.result
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.iconContainer
    }, /*#__PURE__*/React.createElement("img", {
      src: icSuccess
    })), /*#__PURE__*/React.createElement("span", {
      className: styles.title
    }, "Success"), /*#__PURE__*/React.createElement("span", {
      className: styles.description
    }, /*#__PURE__*/React.createElement("span", null, "You have staked\xA0", formatCurrency(totalPrice, {
      withCurrency: true,
      trimWholeFraction: true
    }))), /*#__PURE__*/React.createElement("div", {
      className: styles.buttonGroup
    }, /*#__PURE__*/React.createElement(Button, {
      title: "Stake more",
      type: "warning",
      className: styles.btn,
      onClick: showStakeForm
    })));
  };

  const renderResultFailedContent = () => {
    return /*#__PURE__*/React.createElement("div", {
      className: styles.result
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.iconContainer
    }, /*#__PURE__*/React.createElement("img", {
      src: error === AppError.STAKING_POOL_FULLED ? icMaxPool : icFailure
    })), /*#__PURE__*/React.createElement("span", {
      className: styles.title
    }, error === AppError.STAKING_POOL_FULLED ? 'Max Pool Reached' : 'Failed'), /*#__PURE__*/React.createElement("span", {
      className: classNames(styles.description, styles.errorDescription)
    }, /*#__PURE__*/React.createElement("span", null, formatErrorMessage(error, planStake, endDate))), /*#__PURE__*/React.createElement("div", {
      className: styles.buttonGroup
    }, /*#__PURE__*/React.createElement(Button, {
      title: error === AppError.STAKING_POOL_FULLED ? 'Try later' : 'Try again',
      type: "warning",
      className: styles.btn,
      onClick: showStakeForm
    })));
  };

  const renderStakeForm = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(OptionTab, {
      activeTab: activeTab,
      setActiveTab: setActiveTab,
      planStake: planStake,
      est: est
    }), activeTab == 0 && /*#__PURE__*/React.createElement(HigherRewardContent, {
      numberSelected: numberSelected,
      planStake: planStake,
      totalPrice: totalPrice,
      est: est,
      setPlanStake: setPlanStake,
      showConfirmForm: showConfirmForm,
      chooseHero: openChooseHero,
      thgPerHero: thgPerHero
    }), activeTab == 1 && /*#__PURE__*/React.createElement(FlexibleStakeContent, {
      setAmountInput: setAmountInput,
      doFlexibleStake: doFlexibleStake,
      thgBalance: thgBalance
    }));
  };

  const renderContent = () => {
    switch (showActiveContent) {
      case ActiveContent.STAKE_FORM:
        return /*#__PURE__*/React.createElement(React.Fragment, null, " ", renderStakeForm());

      case ActiveContent.CONFIRM_FORM:
        return /*#__PURE__*/React.createElement(ConfirmStake, {
          numberSelected: numberSelected,
          totalPrice: totalPrice,
          planStake: planStake,
          thgBalance: thgBalance,
          showStakeForm: showStakeForm,
          doHigherRewardStake: doHigherRewardStake
        });

      case ActiveContent.PROCESSING:
        return /*#__PURE__*/React.createElement(React.Fragment, null, " ", renderProcessingContent());

      case ActiveContent.SUCCESS:
        return /*#__PURE__*/React.createElement(React.Fragment, null, " ", renderResultSuccessContent());

      case ActiveContent.FAIL:
        return /*#__PURE__*/React.createElement(React.Fragment, null, " ", renderResultFailedContent());

      default:
        break;
    }
  };

  return /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.container, className)
  }, renderContent());
};

export default StakeForm;